/**
 * Optional icon lookup. `hud/icons.ts` is being written by another builder, so it is loaded
 * through `import.meta.glob` (an empty map when the file does not exist yet) and probed for a
 * resolver function by common names. Callers can always pass their own resolver instead.
 */
import type { AbilityDef } from '@keystone/sim';

export type IconResolver = (ability: AbilityDef) => string | null | undefined;

const CANDIDATES = ['abilityIconUrl', 'iconUrlForAbility', 'abilityIcon', 'iconForAbility', 'getAbilityIcon', 'iconUrl', 'iconFor', 'getIconUrl'] as const;

let cached: IconResolver | null | undefined;

function load(): IconResolver | null {
  let mods: Record<string, unknown> = {};
  try {
    mods = import.meta.glob('../icons.ts', { eager: true }) as Record<string, unknown>;
  } catch {
    mods = {};
  }
  for (const mod of Object.values(mods)) {
    if (!mod || typeof mod !== 'object') continue;
    const rec = mod as Record<string, unknown>;
    for (const name of CANDIDATES) {
      const fn = rec[name];
      if (typeof fn !== 'function') continue;
      const f = fn as (x: unknown) => unknown;
      return (a: AbilityDef) => {
        for (const arg of [a.id, a] as const) {
          try {
            const r = f(arg);
            if (typeof r === 'string' && r) return r;
            if (r && typeof r === 'object' && typeof (r as { url?: unknown }).url === 'string') return (r as { url: string }).url;
          } catch {
            /* try the next argument shape */
          }
        }
        return null;
      };
    }
  }
  return null;
}

/** The shared hud/icons.ts resolver, or null when it is not there (yet). */
export function defaultIconResolver(): IconResolver | null {
  if (cached === undefined) cached = load();
  return cached;
}
